import { XIcon } from '@heroicons/react/solid';
import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import DefaultInput from '@/components/Input';
import { useTranslation } from 'react-i18next';
import Spinner from '@/components/Spinner';
import { toast } from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/state/store';
import Dropdown from '@/components/DropDown';
import TextArea from '@/components/TextArea';
import { useQuery } from '@tanstack/react-query';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { Currency } from '@/enums/Currency';
import { getCurrencyExchange } from '@/services/ExchangeRateService';
import moment from 'moment';
import { formatCurrency } from '@/utils/helpers';
import {
  addSubProjectConsumable,
  updateSubProjectConsumable,
} from '@/state/slices/subProjectMethodSlice';
import { uniqueId } from 'lodash';
import Checkbox from '@/components/Checkbox';
import { EquipmentCategory } from '@/enums/EquipmentCategory';
import { EquipmentResponse, getEquipments } from '@/services/EquipmentService';

export interface ConsumableFormValues {
  id?: string;
  subProjectMethodId?: string;
  equipmentId: string; // Changed to equipmentId
  description: string;
  isLocated: boolean;
  category: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  comment: string;
  exchangeRate: number;
  name?: string;
  totalToCurrency?: number;
  equipmentCurrency?: Currency;
}

interface SubProjectConsumableModalProps {
  closeModal: () => void;
  initialData?: ConsumableFormValues;
}

const validationSchema = Yup.object().shape({
  equipmentId: Yup.string().required(
    'SubProjectConsumableModal.EquipmentIDRequired'
  ),
  isLocated: Yup.boolean().required(
    'SubProjectConsumableModal.IsLocatedRequired'
  ),
  category: Yup.string().required('SubProjectConsumableModal.CategoryRequired'),
  quantity: Yup.number().required('SubProjectConsumableModal.QuantityRequired'),
  unitPrice: Yup.number().required(
    'SubProjectConsumableModal.UnitPriceRequired'
  ),
  comment: Yup.string(),
});

const SubProjectConsumableModal: React.FC<SubProjectConsumableModalProps> = ({
  closeModal,
  initialData,
}) => {
  const { t } = useTranslation();
  const [consumableCurrency, setConsumableCurrency] = useState(Currency.EUR);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const initialValues: ConsumableFormValues = {
    id: initialData?.id ?? '',
    subProjectMethodId: initialData?.subProjectMethodId ?? '',
    equipmentId: initialData?.equipmentId ?? '',
    description: initialData?.description ?? '',
    isLocated: initialData?.isLocated ?? false,
    category: initialData?.category ?? '',
    quantity: initialData?.quantity ?? 0,
    unitPrice: initialData?.unitPrice ?? 0,
    totalPrice: initialData?.totalPrice ?? 0,
    comment: initialData?.comment ?? '',
    exchangeRate: initialData?.exchangeRate ?? 1,
  };

  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { data, status } = useSelector(
    (state: RootState) => state.subProjectMethod!
  );

  const projectCurrency = data?.currency ?? Currency.EUR;

  const handleSave = async (values: ConsumableFormValues) => {
    setLoading(true);
    try {
      const equipment = equipments?.equipments.find(
        (x) => x.id === values.equipmentId
      );
      if (!equipment) {
        toast.error(t('SubProjectConsumableModal.EquipmentNotFound'));
        return;
      }

      const subMeth = {
        ...values,
        id: initialData ? initialData.id : 'newM' + uniqueId(),
        totalPrice: values.unitPrice * values.quantity,
        exchangeRate: values.exchangeRate,
        name: equipment.equipmentName,
        totalToCurrency:
          (values.unitPrice * values.quantity) / values.exchangeRate,
        equipmentCurrency: equipment.currency,
      };

      if (initialData) {
        dispatch(updateSubProjectConsumable(subMeth));
      } else {
        dispatch(addSubProjectConsumable(subMeth));
      }

      toast.success(t('SubProjectConsumableModal.SuccessMessage'));
      closeModal();
    } catch (error) {
      toast.error(t('SubProjectConsumableModal.ErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const {
    isLoading,
    isError,
    data: equipments,
  } = useQuery<EquipmentResponse | undefined>(
    ['equipments'],
    () =>
      getEquipments({
        fromvalue: 0,
        takevalue: 0,
        search: '',
        category: EquipmentCategory.Consommables,
      }),
    { refetchOnWindowFocus: false, staleTime: 6000, keepPreviousData: true }
  );

  const { data: exchangeRates, isLoading: isExchLoading } = useQuery(
    [
      'exchangeRates',
      projectCurrency,
      moment(selectedDate).format('YYYY-MM-DD'),
    ],
    () => getCurrencyExchange(Currency[projectCurrency], selectedDate),
    { staleTime: 60 * (60 * 1000), cacheTime: 120 * (60 * 1000) }
  );

  return (
    <div
      id="popup-modal"
      tabIndex={-1}
      className="fixed left-0 right-0 top-0 z-[1055] flex h-full w-full items-center justify-center overflow-y-auto bg-gray-800 bg-opacity-75"
    >
      <div className="w-full max-w-[35rem] rounded-lg bg-white p-6 shadow-lg dark:bg-gray-700">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {initialData
              ? t('SubProjectConsumableModal.EditConsumable')
              : t('SubProjectConsumableModal.AddConsumable')}
          </h2>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={closeModal}
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>
        {loading || isLoading ? (
          <Spinner />
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSave}
            enableReinitialize={true}
          >
            {({ handleChange, errors, values, setFieldValue }) => (
              <Form>
                <div className="grid gap-2 md:grid-cols-2">
                  <Dropdown
                    id="equipmentId"
                    name="equipmentId"
                    optionValue="id"
                    optionLabel="equipmentName"
                    onChange={(e) => {
                      const eq = equipments?.equipments.find(
                        (x) => x.id === e.target.value
                      );
                      if (!eq) {
                        setFieldValue('equipmentId', '');
                        return;
                      }
                      setConsumableCurrency(eq.currency as Currency);
                      setFieldValue('equipmentId', e.target.value);
                      setFieldValue(
                        'exchangeRate',
                        exchangeRates[Currency[eq.currency as Currency]].value
                      );
                    }}
                    options={equipments?.equipments ?? []}
                    label={t('SubProjectConsumableModal.EquipmentID')}
                    labelClassName="w-[50%]"
                    error={errors.equipmentId}
                    value={values.equipmentId}
                  />
                  <DefaultInput
                    name="description"
                    id="description"
                    value={values.description}
                    type="text"
                    label={t('SubProjectConsumableModal.Description')}
                    errors={errors}
                    onChange={handleChange}
                  />
                  <DefaultInput
                    name="category"
                    id="category"
                    value={values.category}
                    type="text"
                    label={t('SubProjectConsumableModal.Category')}
                    errors={errors}
                    onChange={handleChange}
                  />
                  <DefaultInput
                    name="quantity"
                    id="quantity"
                    value={values.quantity}
                    type="number"
                    label={t('SubProjectConsumableModal.Quantity')}
                    errors={errors}
                    onChange={handleChange}
                  />
                  <DefaultInput
                    name="unitPrice"
                    id="unitPrice"
                    value={values.unitPrice}
                    type="number"
                    label={t('SubProjectConsumableModal.UnitPrice')}
                    errors={errors}
                    onChange={handleChange}
                  />
                  <DefaultInput
                    name="totalPrice"
                    id="totalPrice"
                    value={formatCurrency(
                      values.unitPrice * values.quantity,
                      consumableCurrency
                    )}
                    type="text"
                    label={t('SubProjectConsumableModal.TotalPrice')}
                    errors={errors}
                    disabled={true}
                    onChange={handleChange}
                  />
                  <div className="my-5">
                    <Checkbox
                      name="isLocated"
                      id="isLocated"
                      label={t('SubProjectConsumableModal.IsLocated')}
                      checked={values.isLocated}
                      onChange={(e) =>
                        setFieldValue('isLocated', e.target.checked)
                      }
                    />
                  </div>
                </div>
                <TextArea
                  name="comment"
                  id="comment"
                  value={values.comment}
                  label={t('SubProjectConsumableModal.Comment')}
                  errors={errors}
                  onChange={handleChange}
                />
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    className="mb-2 mr-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:hover:border-gray-600 dark:hover:bg-gray-700 dark:focus:ring-gray-700"
                    onClick={closeModal}
                  >
                    {t('subProjectMaterialModel.Cancel')}
                  </button>
                  <button
                    type="submit"
                    className="mb-2 mr-2 rounded-lg border border-primary-700 px-5 py-2.5 text-center text-sm font-medium text-primary-700 hover:bg-primary-800 hover:text-white focus:outline-none focus:ring-4 focus:ring-primary-300 disabled:border-0 disabled:bg-gray-400 disabled:text-white disabled:hover:bg-none dark:border-primary-500   dark:text-white dark:hover:bg-primary-600 dark:hover:text-white dark:focus:ring-primary-800"
                  >
                    {initialData
                      ? t('SubProjectConsumableModal.Update')
                      : t('SubProjectConsumableModal.Add')}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default SubProjectConsumableModal;
